// Hand-placed dressing (docs/plans/2026-10-17-asset-phases.md, phase 4): parked autos at the stands, and the camp scenes
// of the story mode (tents, durries, the kitchen, books, water, first aid, candles), from <map>.dressing.json. Each row
// stands as a prop (props.ts standing()), depth-sorted with the trees, buildings and bodies; smoke rises only from the
// cooking fires the file names.
import type { GameMap } from '../shared/map';
import type { Standing } from './render';
import { standing, Tier } from './props';
import JANTAR from '../../data/maps/jantar-mantar.dressing.json';

export type Mode = 'all' | 'story';
/** [sprite key, x, y (tiles: the sprite's foot), tier, mode] */
export type Row = [string, number, number, Tier, Mode];
export interface DressingFile { map: string; smoke?: string[]; rows: Row[] }

/** The dressing files bundled with the game, one per map (matched by the map's name). */
export const FILES: DressingFile[] = [JANTAR as unknown as DressingFile];

/** The story mode doesn't exist yet: its rows show with ?story in the page's address. */
export const storyOn = (search = typeof location !== 'undefined' ? location.search : '') => new URLSearchParams(search).has('story');

/** The rows that show on this map: every 'all' row, and the 'story' rows in the story mode. */
export function rowsFor(mapName: string, story: boolean, files = FILES): Row[] {
  return files.filter((f) => f.map === mapName).flatMap((f) => f.rows.filter((r) => r[4] === 'all' || (story && r[4] === 'story')));
}

/** Smoke puffs (public/sprites/smoke.png, four frames of Kenney's CC0 white puff), loaded on first use. */
const SMOKE_SRC = '/sprites/smoke.png', SMOKE_FRAMES = 4;
let smokeImg: HTMLImageElement | null = null, smokeLoading = false;
function smoke(): HTMLImageElement | null {
  if (!smokeImg && !smokeLoading && typeof Image !== 'undefined') {
    smokeLoading = true;
    const i = new Image();
    i.onload = () => { smokeImg = i; };
    i.src = SMOKE_SRC;
  }
  return smokeImg;
}

export class Dressing {
  private items: { x: number; y: number; s: Standing }[] = [];
  /** cooking fires, world px */
  readonly fires: { x: number; y: number }[] = [];
  private readonly metre: number;

  /** The dressing for a map, or null when it has none. */
  static for(m: GameMap, story = storyOn(), files = FILES): Dressing | null {
    const rows = rowsFor(m.name, story, files);
    return rows.length ? new Dressing(m, rows, new Set(files.filter((f) => f.map === m.name).flatMap((f) => f.smoke || []))) : null;
  }

  private constructor(m: GameMap, rows: Row[], smokeFrom: Set<string>) {
    const metre = (this.metre = m.tile / (m.mpt || 1));
    for (const [key, tx, ty, tier] of rows) {
      const x = tx * m.tile, y = ty * m.tile, s = standing(key, x, y, metre, tier);
      if (!s) continue;
      this.items.push({ x, y, s });
      if (smokeFrom.has(key)) this.fires.push({ x, y });
    }
  }

  /** The items near the world rectangle, to sort in with the trees, buildings and bodies. */
  visible(vx0: number, vy0: number, vw: number, vh: number): Standing[] {
    const pad = 12 * this.metre;
    return this.items.filter((i) => i.x > vx0 - pad && i.x < vx0 + vw + pad && i.y > vy0 - pad && i.y < vy0 + vh + 2 * pad).map((i) => i.s);
  }

  /**
   * Thin smoke over each cooking fire, drawn after everything standing: a few faint puffs rising about 6 m and fading,
   * drifting east with the evening air. `t` is seconds. World coordinates.
   */
  drawSmoke(g: CanvasRenderingContext2D, t: number, vx0: number, vw: number, vy0: number, vh: number) {
    const img = smoke();
    if (!img || !this.fires.length) return;
    const M = this.metre, fw = img.width / SMOKE_FRAMES, n = 5, life = 4;
    for (const f of this.fires) {
      if (f.x < vx0 - 20 * M || f.x > vx0 + vw + 20 * M || f.y < vy0 - 20 * M || f.y > vy0 + vh + 20 * M) continue;
      for (let i = 0; i < n; i++) {
        const c = t / life + i / n, k = c - Math.floor(c); // 0 at the fire, 1 at the top; one frame per puff
        const size = (1.6 + 3 * k) * M, rise = (2.2 + 6 * k) * M, drift = (Math.sin(t * 0.7 + i) * 0.4 + 1.5 * k) * M;
        g.globalAlpha = 0.32 * Math.sin(Math.PI * k);
        g.drawImage(img, ((i + Math.floor(c)) % SMOKE_FRAMES) * fw, 0, fw, img.height, f.x + drift - size / 2, f.y - rise - size / 2, size, size);
      }
    }
    g.globalAlpha = 1;
  }
}
